import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, PenLine, Building2 } from "lucide-react";

function SplitSection({ title, icon: Icon, options, splits, setSplits, testPrefix }) {
  const total = splits.reduce((a, s) => a + (parseFloat(s.percentage) || 0), 0);
  const add = () => setSplits([...splits, { entity_id: null, name: "", percentage: 0 }]);
  const update = (i, patch) => setSplits(splits.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => setSplits(splits.filter((_, idx) => idx !== i));

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <h3 className="font-heading text-lg font-semibold">{title}</h3>
        </div>
        <span data-testid={`${testPrefix}-total`} className={`font-mono text-sm font-semibold ${Math.abs(total - 100) < 0.01 ? "text-primary" : "text-amber-400"}`}>
          {total.toFixed(2)}%
        </span>
      </div>
      <div className="space-y-3">
        {splits.map((s, i) => (
          <div key={i} className="flex gap-3 items-center" data-testid={`${testPrefix}-row-${i}`}>
            <Select value={s.name} onValueChange={(v) => update(i, { name: v })}>
              <SelectTrigger data-testid={`${testPrefix}-select-${i}`} className="bg-secondary border-border flex-1"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}{o.pro ? ` · ${o.pro}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative w-28">
              <Input data-testid={`${testPrefix}-pct-${i}`} type="number" value={s.percentage} onChange={(e) => update(i, { percentage: e.target.value })} className="bg-secondary border-border font-mono text-right pr-6" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
            </div>
            <button onClick={() => remove(i)} data-testid={`${testPrefix}-remove-${i}`} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden mt-2">
          <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${Math.min(total, 100)}%` }} />
        </div>
        <Button variant="outline" size="sm" onClick={add} data-testid={`${testPrefix}-add`} className="border-border bg-secondary hover:bg-zinc-800 mt-2">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

export default function SongDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [writers, setWriters] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [form, setForm] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/songs/${id}`),
      api.get("/writers"),
      api.get("/publishers"),
    ]).then(([s, w, p]) => {
      setSong(s.data);
      setForm({
        title: s.data.title, isrc: s.data.isrc || "", iswc: s.data.iswc || "", notes: s.data.notes || "",
        writer_splits: s.data.writer_splits || [], publisher_splits: s.data.publisher_splits || [],
      });
      setWriters(w.data);
      setPublishers(p.data);
    }).catch(() => { toast.error("Song not found"); navigate("/songs"); });
  }, [id, navigate]);

  const save = async () => {
    try {
      await api.put(`/songs/${id}`, form);
      toast.success("Song saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!form) return null;

  return (
    <div data-testid="song-detail-page">
      <button onClick={() => navigate("/songs")} data-testid="back-btn" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 mb-6 text-sm transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Songs
      </button>

      <div className="flex items-start justify-between mb-8 gap-4">
        <div className="flex-1">
          <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Song</h4>
          <input
            data-testid="detail-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="font-heading text-4xl font-black tracking-tighter uppercase bg-transparent border-none outline-none w-full focus:text-primary transition-colors"
          />
        </div>
        <Button onClick={save} data-testid="save-detail-btn" className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 shrink-0">
          <Save className="h-4 w-4 mr-2" /> Save
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-card border border-border rounded-lg p-6">
          <Label className="text-xs uppercase tracking-widest text-zinc-400">ISRC</Label>
          <Input data-testid="detail-isrc-input" value={form.isrc} onChange={(e) => setForm({ ...form, isrc: e.target.value })} className="mt-2 bg-secondary border-border font-mono" placeholder="US-XXX-YY-NNNNN" />
        </div>
        <div className="bg-card border border-border rounded-lg p-6">
          <Label className="text-xs uppercase tracking-widest text-zinc-400">ISWC</Label>
          <Input data-testid="detail-iswc-input" value={form.iswc} onChange={(e) => setForm({ ...form, iswc: e.target.value })} className="mt-2 bg-secondary border-border font-mono" placeholder="T-000000000-0" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SplitSection title="Writer Splits" icon={PenLine} options={writers} testPrefix="writer-split"
          splits={form.writer_splits} setSplits={(v) => setForm({ ...form, writer_splits: v })} />
        <SplitSection title="Publisher Splits" icon={Building2} options={publishers} testPrefix="publisher-split"
          splits={form.publisher_splits} setSplits={(v) => setForm({ ...form, publisher_splits: v })} />
      </div>
    </div>
  );
}
